import React from "react";
import { Calendar, Receipt } from "lucide-react";
import {
  formatDateMonthYear,
  formatDateMonthYearShort,
} from "@utils/formatDate";
import { formatNumberDecimals } from "@utils/formatNumber";
import { usePartnerStore } from "@store/partnerStore";
import { Invoice } from "./InvoiceTableColumns";
import { Skeleton, Tooltip } from "@components";

interface NextUpcomingInvoiceWidgetProps {
  invoice: Invoice | undefined;
  isLoading: boolean;
}

export const NextUpcomingInvoiceWidget: React.FC<
  NextUpcomingInvoiceWidgetProps
> = ({ invoice, isLoading }) => {
  const { currency } = usePartnerStore();

  const tooltipText =
    "Some orders or returns may be processed after a month given has ended. When this happens, those late updates are included in the following invoice - so you may see charges for the previous period. Likewise, if any returns are reported late, we’ll automatically credit them back on the next invoice.";

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 w-1/2">
        <div className="flex items-center gap-3 mb-4">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <Skeleton className="h-6 w-48" />
        </div>

        <div className="space-y-4">
          <div className="flex items-baseline gap-2">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-6 w-12" />
          </div>

          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-4 w-40" />
          </div>

          <div className="pt-4 border-t border-gray-200">
            <Skeleton className="h-5 w-64 mb-3" />
            <div className="space-y-2">
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-gray-100">
            <Receipt className="w-5 h-5 text-gray-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Next Upcoming Payment
          </h2>
        </div>
        <p className="text-sm text-gray-500">No upcoming invoices</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 w-1/2">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-100">
            <Receipt className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Next Upcoming Payment
          </h2>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold text-gray-900">
            {formatNumberDecimals(invoice.total)}
          </span>
          {currency && (
            <span className="text-lg text-gray-600 font-medium">
              {currency}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>Period: {formatDateMonthYear(invoice.periodEnd)}</span>
        </div>

        {invoice.months && invoice.months.length > 0 && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-700 mb-3">
              Monthly Breakdown
              <Tooltip
                width="w-96"
                text={tooltipText}
              >
                <span className="text-xs text-gray-500 font-normal ml-2 hover:underline cursor-pointer">
                  Why do I see amounts for previous months in this month's invoice?
                </span>
              </Tooltip>
            </p>
            <div className="space-y-2">
              {invoice.months.map((month, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="text-sm font-medium text-gray-900">
                    {formatDateMonthYearShort(month.month)}
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {formatNumberDecimals(month.amount)}
                    {currency && (
                      <span className="ml-1 text-gray-600">{currency}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
